import React from "react";
import { Box } from "@mui/material";
import RoomServiceRoundedIcon from "@mui/icons-material/RoomServiceRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import DoNotDisturbAltRoundedIcon from "@mui/icons-material/DoNotDisturbAltRounded";
import { BUTTON_RESET, C, FONT_BODY, FONT_DISPLAY, VISUALLY_HIDDEN } from "../../../utils/designTokens";
import { OD } from "./detailUi";

const LOOK = {
  live: { bg: C.red, ink: OD.cancelledInk, Icon: RoomServiceRoundedIcon },
  complete: { bg: C.green, ink: C.green, Icon: CheckRoundedIcon },
  cancelled: { bg: OD.cancelledBanner, ink: OD.cancelledBanner, Icon: DoNotDisturbAltRoundedIcon },
};

/**
 * The coloured status banner (replaces the dark tracker). `track` is what
 * "Track live →" opens: { href } for a courier's tracking page, { onClick }
 * for the live map on this page; none for a cancelled order. `trackDriverHref`
 * is First Delivery's second link, the driver's own tracker, shown beside it.
 */
function StatusBanner({ banner, track, trackDriverHref }) {
  const look = LOOK[banner.tone] || LOOK.live;
  const Icon = look.Icon;
  const trackSx = {
    ...BUTTON_RESET,
    boxSizing: "border-box",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 1,
    height: 52,
    px: 2.75,
    borderRadius: "16px",
    bgcolor: "#FFFFFF",
    color: look.ink,
    fontFamily: FONT_BODY,
    fontSize: 15,
    fontWeight: 800,
    textDecoration: "none",
    whiteSpace: "nowrap",
    transition: "transform .15s ease, box-shadow .15s ease",
    "&:hover": { transform: "translateY(-1px)", boxShadow: "0 6px 16px rgba(0,0,0,0.18)" },
    "&:focus-visible": { outline: "3px solid #FFFFFF", outlineOffset: "3px" },
  };

  return (
    <Box
      component="section"
      aria-label="Order status"
      sx={{
        position: "relative",
        overflow: "hidden",
        borderRadius: "26px",
        p: { xs: "22px", sm: "26px 28px" },
        bgcolor: look.bg,
        color: "#FFFFFF",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        // When the tiles and buttons don't fit beside the headline, they take the next row.
        flexWrap: { md: "wrap" },
        alignItems: { xs: "stretch", md: "center" },
        gap: { xs: 2.5, md: 3 },
        // One translucent circle, top right.
        "&::before": { content: '""', position: "absolute", width: 240, height: 240, top: -110, right: -70, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.08)", pointerEvents: "none" },
      }}
    >
      <Box sx={{ position: "relative", display: "flex", alignItems: "center", gap: 2.25, flex: { md: "1 1 420px" }, minWidth: 0 }}>
        <Box sx={{ width: 64, height: 64, flexShrink: 0, borderRadius: "20px", bgcolor: "rgba(255,255,255,0.16)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon aria-hidden="true" sx={{ fontSize: 32 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ fontFamily: FONT_BODY, fontSize: 13, fontWeight: 700, letterSpacing: "0.06em", color: banner.tone === "live" ? OD.lightRedText : "rgba(255,255,255,0.85)" }}>
            {banner.eyebrow}
          </Box>
          <Box component="p" role="status" sx={{ m: 0, mt: 0.5, fontFamily: FONT_DISPLAY, fontSize: { xs: 24, sm: 30 }, fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.12 }}>
            {banner.headline}
          </Box>
          {banner.sub && (
            <Box component="p" sx={{ m: 0, mt: 0.75, fontFamily: FONT_BODY, fontSize: 14, lineHeight: 1.45, color: "rgba(255,255,255,0.9)" }}>
              {banner.sub}
            </Box>
          )}
        </Box>
      </Box>

      {(banner.tiles.length > 0 || track || trackDriverHref) && (
        <Box sx={{ position: "relative", display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.25, justifyContent: { md: "flex-end" }, flex: { md: "0 0 auto" }, ml: { md: "auto" } }}>
          {banner.tiles.length > 0 && (
            <Box component="dl" sx={{ m: 0, display: "flex", flexWrap: "wrap", gap: 1.25 }}>
              {banner.tiles.map((t) => (
                <Box key={t.label} sx={{ minWidth: 92, px: 1.75, py: 1.25, borderRadius: "14px", bgcolor: "rgba(255,255,255,0.14)" }}>
                  <Box component="dt" sx={{ fontFamily: FONT_BODY, fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.85)" }}>
                    {t.label}
                  </Box>
                  <Box component="dd" sx={{ m: 0, mt: 0.25, fontFamily: FONT_BODY, fontSize: 18, fontWeight: 800, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                    {t.value}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
          {track &&
            (track.href ? (
              <Box component="a" href={track.href} target="_blank" rel="noopener noreferrer" sx={trackSx}>
                Track live <span aria-hidden="true">→</span>
                <Box component="span" sx={VISUALLY_HIDDEN}> (opens in a new tab)</Box>
              </Box>
            ) : (
              <Box component="button" type="button" onClick={track.onClick} sx={trackSx}>
                Track live <span aria-hidden="true">→</span>
              </Box>
            ))}
          {trackDriverHref && (
            <Box
              component="a"
              href={trackDriverHref}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ ...trackSx, bgcolor: "transparent", color: "#FFFFFF", border: "1.5px solid rgba(255,255,255,0.85)", "&:hover": { bgcolor: "rgba(255,255,255,0.12)" } }}
            >
              Track driver <span aria-hidden="true">→</span>
              <Box component="span" sx={VISUALLY_HIDDEN}> (opens in a new tab)</Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

export default StatusBanner;
